import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Plus, RefreshCw, Trash2, Type, X } from 'react-feather';
import TextareaAutosize from 'react-textarea-autosize';
import api from '../api';
import { trackEvent } from '../utils/amplitude';
import { formatThumbnailTitleForRender } from '../utils/thumbnailTitleText';
import { newSlideId, redistributeSlideTexts, splitTextIntoSlides } from '../utils/carouselSlides';

const SLIDE_COUNT_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10];
const PREVIEW_ASPECT = '4 / 5';
const EXPORT_W = 1080;
const EXPORT_H = 1350;
const COLOR_GRADE = 'contrast(1.25) saturate(1.3) brightness(0.98) sepia(0.05) hue-rotate(-4deg)';

function SparklesIcon({ size = 16 }) {
  const tip = (cx, cy, L, w, r) => [
    `M${cx} ${cy - L}`,
    `L${cx + w} ${cy - w - r}`,
    `A${r} ${r} 0 0 0 ${cx + w + r} ${cy - w}`,
    `L${cx + L} ${cy}`,
    `L${cx + w + r} ${cy + w}`,
    `A${r} ${r} 0 0 0 ${cx + w} ${cy + w + r}`,
    `L${cx} ${cy + L}`,
    `L${cx - w} ${cy + w + r}`,
    `A${r} ${r} 0 0 0 ${cx - w - r} ${cy + w}`,
    `L${cx - L} ${cy}`,
    `L${cx - w - r} ${cy - w}`,
    `A${r} ${r} 0 0 0 ${cx - w} ${cy - w - r}`,
    'Z',
  ].join(' ');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={tip(10.2, 9, 7.6, 2.0, 1.55)} />
      <path d={tip(18.4, 17.8, 3.6, 0.95, 0.8)} />
    </svg>
  );
}

function pickRandomCut(pool, excludeId) {
  if (!Array.isArray(pool) || pool.length === 0) return null;
  let candidates = pool;
  if (excludeId && pool.length > 1) {
    const filtered = pool.filter((c) => String(c._id || c.id) !== String(excludeId));
    if (filtered.length) candidates = filtered;
  }
  return candidates[Math.floor(Math.random() * candidates.length)] || null;
}

function loadMediaAsImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No media url'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}

function captureVideoFrame(url) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const cleanup = () => {
      video.removeAttribute('src');
      try { video.load(); } catch (_) { /* ignore */ }
    };
    video.onloadeddata = () => {
      try {
        video.currentTime = Math.min(0.05, (video.duration || 1) * 0.01);
      } catch (e) {
        cleanup();
        reject(e);
      }
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || EXPORT_W;
        canvas.height = video.videoHeight || EXPORT_H;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        cleanup();
        resolve(canvas);
      } catch (e) {
        cleanup();
        reject(e);
      }
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Video load failed'));
    };
    video.src = url;
  });
}

function drawCover(ctx, source, dw, dh) {
  const sw = source.width || source.videoWidth || dw;
  const sh = source.height || source.videoHeight || dh;
  const scale = Math.max(dw / sw, dh / sh);
  const tw = sw * scale;
  const th = sh * scale;
  const ox = (dw - tw) / 2;
  const oy = (dh - th) / 2;
  ctx.drawImage(source, ox, oy, tw, th);
}

function wrapExportText(ctx, text, maxWidth) {
  const lines = [];
  const paragraphs = String(text || '').split('\n');
  for (const para of paragraphs) {
    const words = para.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      continue;
    }
    let line = words[0];
    for (let i = 1; i < words.length; i++) {
      const next = `${line} ${words[i]}`;
      if (ctx.measureText(next).width <= maxWidth) line = next;
      else {
        lines.push(line);
        line = words[i];
      }
    }
    lines.push(line);
  }
  return lines;
}

async function renderSlideToPng({ cut, text, fontFamily, fontColor, outlineColor, bigText }) {
  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_W;
  canvas.height = EXPORT_H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);

  let drawn = false;
  const thumb = cut?.thumbnailUrl;
  if (thumb) {
    try {
      const img = await loadMediaAsImage(thumb);
      drawCover(ctx, img, EXPORT_W, EXPORT_H);
      drawn = true;
    } catch (_) { /* fall through */ }
  }
  if (!drawn && cut?.url) {
    try {
      const frame = await captureVideoFrame(cut.url);
      drawCover(ctx, frame, EXPORT_W, EXPORT_H);
      drawn = true;
    } catch (_) { /* keep solid bg */ }
  }

  // Mild grade overlay approximation
  ctx.fillStyle = 'rgba(20, 12, 0, 0.08)';
  ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);

  const display = formatThumbnailTitleForRender(text, bigText !== false);
  if (display.trim()) {
    const fontSize = bigText !== false ? 92 : 64;
    ctx.font = `bold ${fontSize}px ${fontFamily || 'Arial, sans-serif'}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const maxWidth = EXPORT_W * 0.78;
    const lines = wrapExportText(ctx, display, maxWidth);
    const lineHeight = fontSize * 1.2;
    const blockH = lines.length * lineHeight;
    let y = (EXPORT_H - blockH) / 2 + lineHeight / 2;
    const outline = outlineColor || '#000000';
    for (const line of lines) {
      ctx.lineWidth = Math.max(4, fontSize * 0.08);
      ctx.strokeStyle = outline;
      ctx.fillStyle = fontColor || '#ffffff';
      ctx.strokeText(line, EXPORT_W / 2, y);
      ctx.fillText(line, EXPORT_W / 2, y);
      y += lineHeight;
    }
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function downloadBlob(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Studio → Carousel: build a social carousel from workspace cut thumbnails + slide text.
 */
export default function StudioCarousel({
  workspaceId,
  darkMode,
  screenWidth = 1200,
  availableHooks = [],
  userFonts = [],
  defaultFontFamilyCss,
  fontFamilyCssForName,
  generationLanguage,
  setGenerationLanguage,
  languageOptions = [],
  showGenerationLanguageDropdown,
  setShowGenerationLanguageDropdown,
  loadCuts,
  onError,
}) {
  const [hook, setHook] = useState('');
  const [topic, setTopic] = useState('');
  const [showHookInput, setShowHookInput] = useState(false);
  const [showTopicInput, setShowTopicInput] = useState(true);
  const [slideCount, setSlideCount] = useState(5);
  const [unlocked, setUnlocked] = useState(false);
  const [slides, setSlides] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cuts, setCuts] = useState([]);
  const [cutsLoading, setCutsLoading] = useState(false);
  const [cutPickerOpen, setCutPickerOpen] = useState(false);
  const [fontId, setFontId] = useState('');
  const [fontColor, setFontColor] = useState('#ffffff');
  const [outlineColor, setOutlineColor] = useState('#000000');
  const [bigText, setBigText] = useState(true);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [manualDraft, setManualDraft] = useState('');

  const sortedFonts = useMemo(
    () => [...(userFonts || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })),
    [userFonts]
  );

  const activeFontFamily = fontId
    ? fontFamilyCssForName?.(userFonts.find((f) => String(f._id) === String(fontId))?.name) || defaultFontFamilyCss
    : defaultFontFamilyCss;

  const selected = slides[selectedIndex] || null;

  useEffect(() => {
    if (hook || !availableHooks.length) return;
    setHook(availableHooks[Math.floor(Math.random() * availableHooks.length)]);
  }, [availableHooks, hook]);

  useEffect(() => {
    if (!showFontDropdown) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest('[data-carousel-font-dropdown]')) setShowFontDropdown(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showFontDropdown]);

  const ensureCuts = useCallback(async () => {
    if (cuts.length > 0) return cuts;
    if (!loadCuts) return [];
    setCutsLoading(true);
    try {
      const loaded = await loadCuts();
      const list = Array.isArray(loaded) ? loaded : [];
      setCuts(list);
      return list;
    } catch (e) {
      onError?.(`Failed to load cuts: ${e.message || e}`);
      return [];
    } finally {
      setCutsLoading(false);
    }
  }, [cuts, loadCuts, onError]);

  const buildSlidesFromTexts = useCallback(async (texts, cutPool) => {
    const pool = cutPool?.length ? cutPool : await ensureCuts();
    const used = new Set();
    return texts.map((text) => {
      let cut = pickRandomCut(pool.filter((c) => !used.has(String(c._id || c.id))), null);
      if (!cut) cut = pickRandomCut(pool, null);
      if (cut) used.add(String(cut._id || cut.id));
      return { id: newSlideId(), text: String(text || ''), cut };
    });
  }, [ensureCuts]);

  const selectRandomHook = () => {
    if (!availableHooks.length) return;
    setHook(availableHooks[Math.floor(Math.random() * availableHooks.length)]);
  };

  const handleGenerate = async () => {
    if (!workspaceId) return;
    setGenerating(true);
    try {
      const pool = await ensureCuts();
      const res = await api.generateCarouselSlides(workspaceId, {
        topic: topic.trim() || undefined,
        hook: hook.trim() || undefined,
        slideCount,
        language: generationLanguage || 'en',
        existingText: manualDraft.trim() || undefined,
      });
      const texts = Array.isArray(res.data?.slides) ? res.data.slides : splitTextIntoSlides('', slideCount);
      const next = await buildSlidesFromTexts(texts, pool);
      setSlides(next);
      setSelectedIndex(0);
      setUnlocked(true);
      setManualDraft('');
      trackEvent('studio_carousel_generated', {
        workspaceId,
        slideCount: next.length,
        language: generationLanguage || 'en',
      });
    } catch (e) {
      onError?.(`Failed to generate carousel: ${e.response?.data?.error || e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleUseOwnText = async () => {
    const pool = await ensureCuts();
    const texts = manualDraft.trim()
      ? splitTextIntoSlides(manualDraft, slideCount)
      : Array.from({ length: slideCount }, () => '');
    const next = await buildSlidesFromTexts(texts, pool);
    setSlides(next);
    setSelectedIndex(0);
    setUnlocked(true);
  };

  const applySlideCount = async (count) => {
    const n = Math.max(2, Math.min(20, Number(count) || 5));
    setSlideCount(n);
    if (!unlocked || slides.length === 0) return;
    const texts = redistributeSlideTexts(slides.map((s) => s.text), n);
    const pool = await ensureCuts();
    const next = [];
    for (let i = 0; i < n; i++) {
      const prev = slides[i];
      next.push({
        id: prev?.id || newSlideId(),
        text: texts[i] || '',
        cut: prev?.cut || pickRandomCut(pool, null),
      });
    }
    setSlides(next);
    setSelectedIndex((idx) => Math.min(idx, n - 1));
  };

  const updateSelectedText = (text) => {
    setSlides((prev) => prev.map((s, i) => (i === selectedIndex ? { ...s, text } : s)));
  };

  const setSelectedCut = (cut) => {
    setSlides((prev) => prev.map((s, i) => (i === selectedIndex ? { ...s, cut } : s)));
    setCutPickerOpen(false);
  };

  const randomizeSelectedCut = async () => {
    const pool = await ensureCuts();
    const cut = pickRandomCut(pool, selected?.cut?._id || selected?.cut?.id);
    if (cut) setSelectedCut(cut);
  };

  const randomizeAllCuts = async () => {
    const pool = await ensureCuts();
    if (!pool.length) return;
    const used = new Set();
    setSlides((prev) => prev.map((s) => {
      let cut = pickRandomCut(pool.filter((c) => !used.has(String(c._id || c.id))), null);
      if (!cut) cut = pickRandomCut(pool, null);
      if (cut) used.add(String(cut._id || cut.id));
      return { ...s, cut };
    }));
  };

  const addSlide = async () => {
    if (slides.length >= 20) return;
    const pool = await ensureCuts();
    setSlides((prev) => [...prev, { id: newSlideId(), text: '', cut: pickRandomCut(pool, null) }]);
    setSlideCount((c) => Math.min(20, c + 1));
    setSelectedIndex(slides.length);
  };

  const removeSlide = (index) => {
    if (slides.length <= 2) return;
    setSlides((prev) => prev.filter((_, i) => i !== index));
    setSlideCount((c) => Math.max(2, c - 1));
    setSelectedIndex((idx) => {
      if (index < idx) return idx - 1;
      if (index === idx) return Math.max(0, idx - 1);
      return idx;
    });
  };

  const moveSlide = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= slides.length) return;
    setSlides((prev) => {
      const next = [...prev];
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
    setSelectedIndex(target);
  };

  const handleExport = async () => {
    if (!slides.length) return;
    setExporting(true);
    try {
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const blob = await renderSlideToPng({
          cut: slide.cut,
          text: slide.text,
          fontFamily: activeFontFamily,
          fontColor,
          outlineColor,
          bigText,
        });
        downloadBlob(blob, `carousel-slide-${i + 1}.png`);
        // Small gap so browsers don't coalesce downloads
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250));
      }
      trackEvent('studio_carousel_exported', { workspaceId, slideCount: slides.length });
    } catch (e) {
      onError?.(`Failed to export slides: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const isNarrow = screenWidth <= 600;
  const isTiny = screenWidth <= 450;

  const textShadow = outlineColor
    ? `0 0 4px ${outlineColor}, 0 0 6px ${outlineColor}, 0 1px 2px ${outlineColor}, 1px 1px 0 ${outlineColor}, -1px -1px 0 ${outlineColor}, 1px -1px 0 ${outlineColor}, -1px 1px 0 ${outlineColor}`
    : 'none';

  const section = (children, extra = {}) => (
    <div
      style={{
        backgroundColor: 'var(--input-bg)',
        border: '1px solid var(--input-border)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        ...extra,
      }}
    >
      {children}
    </div>
  );

  return (
    <div>
      {section(
        <>
          <h4 style={{ marginTop: 0, whiteSpace: isNarrow ? 'nowrap' : 'normal' }}>
            1 - Theme
            <span
              style={{
                marginLeft: '8px',
                fontSize: '12px',
                fontWeight: 'normal',
                background: 'linear-gradient(45deg, #ff6b6b, #ff8e8e)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              @CarouselWriter
            </span>
          </h4>

          <div style={{ marginBottom: '12px' }}>
            <label
              onClick={() => setShowHookInput(!showHookInput)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                userSelect: 'none',
                color: 'var(--text-primary)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  transform: showHookInput ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  fontSize: '10px',
                  color: 'var(--text-secondary)',
                }}
              >
                ▶
              </span>
              {showHookInput ? 'Hide hook' : 'See hook'}
            </label>
            {showHookInput && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexDirection: isNarrow ? 'column' : 'row',
                  alignItems: isNarrow ? 'stretch' : 'center',
                }}
              >
                <TextareaAutosize
                  value={hook}
                  onChange={(e) => setHook(e.target.value)}
                  placeholder="Hook / angle for the carousel..."
                  minRows={2}
                  style={{
                    flex: 1,
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid var(--input-border)',
                    background: 'var(--input-bg)',
                    color: 'var(--input-text)',
                    fontSize: '14px',
                    resize: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  type="button"
                  onClick={selectRandomHook}
                  disabled={availableHooks.length === 0}
                  style={{
                    padding: '8px 12px',
                    fontSize: '12px',
                    backgroundColor: 'var(--button-bg)',
                    color: 'var(--button-text)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    cursor: availableHooks.length > 0 ? 'pointer' : 'not-allowed',
                    opacity: availableHooks.length > 0 ? 1 : 0.5,
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Refresh Hook
                </button>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label
              onClick={() => setShowTopicInput(!showTopicInput)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                userSelect: 'none',
                color: 'var(--text-primary)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  transform: showTopicInput ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  fontSize: '10px',
                  color: 'var(--text-secondary)',
                }}
              >
                ▶
              </span>
              Carousel topic
            </label>
            {showTopicInput && (
              <TextareaAutosize
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="What is this carousel about?"
                minRows={2}
                maxRows={8}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  resize: 'none',
                  fontFamily: 'inherit',
                }}
              />
            )}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>Number of slides</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {SLIDE_COUNT_OPTIONS.map((n) => {
                const selectedN = slideCount === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => applySlideCount(n)}
                    style={{
                      minWidth: '40px',
                      padding: '8px 10px',
                      borderRadius: '10px',
                      border: selectedN
                        ? '1px solid rgba(225, 246, 121, 0.55)'
                        : (darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.12)'),
                      backgroundColor: selectedN
                        ? (darkMode ? 'rgba(225, 246, 121, 0.10)' : 'rgba(200, 230, 80, 0.22)')
                        : (darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
                      color: selectedN ? (darkMode ? '#e1f679' : '#3d4f00') : 'var(--text-secondary)',
                      fontSize: '13px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            {unlocked && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Changing the count re-cuts your text across the new slides.
              </div>
            )}
          </div>

          {!unlocked && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Optional: paste text to split / rewrite
              </label>
              <TextareaAutosize
                value={manualDraft}
                onChange={(e) => setManualDraft(e.target.value)}
                placeholder="Paste a draft and we’ll cut it into slides…"
                minRows={2}
                maxRows={6}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  resize: 'none',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: '10px',
              marginTop: '10px',
              alignItems: 'center',
              flexDirection: isTiny ? 'column' : 'row',
            }}
          >
            <div style={{ position: 'relative', width: isTiny ? '100%' : 'auto' }} data-generation-language-dropdown>
              <button
                type="button"
                onClick={() => setShowGenerationLanguageDropdown?.(!showGenerationLanguageDropdown)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  minWidth: '120px',
                  width: isTiny ? '100%' : 'auto',
                }}
              >
                <span>🌐</span>
                <span>{languageOptions.find((l) => l.code === generationLanguage)?.name || 'English'}</span>
                <span style={{ fontSize: '10px', opacity: 0.7 }}>
                  {showGenerationLanguageDropdown ? '▲' : '▼'}
                </span>
              </button>
              {showGenerationLanguageDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    marginTop: '2px',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                >
                  {languageOptions.map((lang) => (
                    <div
                      key={lang.code}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: generationLanguage === lang.code ? 'var(--button-bg)' : 'transparent',
                        color: generationLanguage === lang.code ? 'var(--button-text)' : 'var(--text-color)',
                        fontSize: '12px',
                      }}
                      onClick={() => {
                        setGenerationLanguage?.(lang.code);
                        setShowGenerationLanguageDropdown?.(false);
                      }}
                    >
                      {lang.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="main-button"
              onClick={handleGenerate}
              disabled={generating || (!hook.trim() && !topic.trim() && !manualDraft.trim())}
              style={{
                flex: 1,
                width: isTiny ? '100%' : 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                opacity: (generating || (!hook.trim() && !topic.trim() && !manualDraft.trim())) ? 0.7 : 1,
              }}
            >
              {generating ? (
                <><span className="spinner-border" /> Generating slides…</>
              ) : (
                <><SparklesIcon size={16} /> Generate carousel text</>
              )}
            </button>
          </div>

          {!unlocked && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                OR
              </div>
              <button
                type="button"
                style={{ width: '100%', justifyContent: 'center', textAlign: 'center', fontWeight: 'bold' }}
                className="tertiary-button"
                onClick={handleUseOwnText}
              >
                Use your own text
              </button>
            </div>
          )}
        </>
      )}

      {unlocked && (
        <>
          {section(
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0 }}>
                  2 - Slides
                  <span
                    style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      fontWeight: 'normal',
                      background: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    @SlideEditor
                  </span>
                </h4>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={randomizeAllCuts}
                    disabled={cutsLoading}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--input-border)',
                      background: 'var(--input-bg)',
                      color: 'var(--text-color)',
                      cursor: cutsLoading ? 'not-allowed' : 'pointer',
                    }}
                    title="Shuffle cuts on all slides"
                  >
                    <RefreshCw size={14} />
                    Shuffle cuts
                  </button>
                  <button
                    type="button"
                    onClick={addSlide}
                    disabled={slides.length >= 20}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--input-border)',
                      background: 'var(--input-bg)',
                      color: 'var(--text-color)',
                      cursor: slides.length >= 20 ? 'not-allowed' : 'pointer',
                      opacity: slides.length >= 20 ? 0.55 : 1,
                    }}
                  >
                    <Plus size={14} />
                    Add slide
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  overflowX: 'auto',
                  paddingBottom: '8px',
                  scrollSnapType: 'x mandatory',
                }}
              >
                {slides.map((slide, index) => {
                  const active = index === selectedIndex;
                  return (
                    <button
                      key={slide.id}
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      style={{
                        flex: '0 0 auto',
                        width: isNarrow ? '140px' : '168px',
                        padding: 0,
                        border: active ? '2px solid #D7F06D' : '2px solid var(--input-border)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: '#0a0a0a',
                        cursor: 'pointer',
                        scrollSnapAlign: 'start',
                        position: 'relative',
                      }}
                    >
                      <div style={{ position: 'relative', width: '100%', aspectRatio: PREVIEW_ASPECT, background: '#111' }}>
                        {slide.cut?.thumbnailUrl ? (
                          <img
                            src={slide.cut.thumbnailUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: COLOR_GRADE }}
                          />
                        ) : slide.cut?.url ? (
                          <video
                            src={slide.cut.url}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: COLOR_GRADE }}
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 12 }}>
                            No cut
                          </div>
                        )}
                        {String(slide.text || '').trim() && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '10px',
                              textAlign: 'center',
                              pointerEvents: 'none',
                            }}
                          >
                            <span
                              style={{
                                color: fontColor,
                                fontSize: bigText ? '18px' : '14px',
                                fontWeight: 'bold',
                                fontFamily: activeFontFamily,
                                lineHeight: 1.25,
                                whiteSpace: 'pre-line',
                                textShadow,
                                maxWidth: '100%',
                              }}
                            >
                              {formatThumbnailTitleForRender(slide.text, bigText)}
                            </span>
                          </div>
                        )}
                        <div
                          style={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            background: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: 999,
                          }}
                        >
                          {index + 1}/{slides.length}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {selected && section(
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0 }}>3 - Edit slide {selectedIndex + 1}</h4>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => moveSlide(selectedIndex, -1)}
                    disabled={selectedIndex === 0}
                    style={iconBtnStyle(selectedIndex === 0)}
                    title="Move left"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSlide(selectedIndex, 1)}
                    disabled={selectedIndex >= slides.length - 1}
                    style={iconBtnStyle(selectedIndex >= slides.length - 1)}
                    title="Move right"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSlide(selectedIndex)}
                    disabled={slides.length <= 2}
                    style={iconBtnStyle(slides.length <= 2)}
                    title="Remove slide"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <TextareaAutosize
                value={selected.text}
                onChange={(e) => updateSelectedText(e.target.value)}
                placeholder="Text on this slide…"
                minRows={3}
                maxRows={8}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--input-text)',
                  fontSize: '14px',
                  lineHeight: 1.4,
                  fontFamily: activeFontFamily,
                  boxSizing: 'border-box',
                  resize: 'none',
                  marginBottom: '12px',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
                {cutsLoading && !selected.cut ? (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Loading cut…</span>
                ) : selected.cut ? (
                  <div
                    style={{
                      width: '72px',
                      aspectRatio: PREVIEW_ASPECT,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid var(--input-border)',
                      flexShrink: 0,
                    }}
                  >
                    {selected.cut.thumbnailUrl ? (
                      <img src={selected.cut.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <video src={selected.cut.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted preload="metadata" />
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>No cut</span>
                )}
                <button type="button" onClick={randomizeSelectedCut} disabled={cutsLoading} style={secondaryBtnStyle}>
                  <RefreshCw size={14} style={{ marginRight: 6 }} />
                  Random cut
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await ensureCuts();
                    setCutPickerOpen(true);
                  }}
                  style={secondaryBtnStyle}
                >
                  Change cut
                </button>
              </div>
            </>
          )}

          {section(
            <>
              <h4 style={{ marginTop: 0, marginBottom: '12px' }}>
                <Type size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
                4 - Text style
              </h4>

              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px' }}>Font</label>
              <div style={{ position: 'relative', marginBottom: '14px' }} data-carousel-font-dropdown>
                <button
                  type="button"
                  onClick={() => setShowFontDropdown(!showFontDropdown)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--input-border)',
                    background: 'var(--input-bg)',
                    color: 'var(--input-text)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontFamily: activeFontFamily }}>
                    {fontId
                      ? userFonts.find((f) => String(f._id) === String(fontId))?.name || 'Select font...'
                      : 'Default (TikTok medium)'}
                  </span>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>{showFontDropdown ? '▲' : '▼'}</span>
                </button>
                {showFontDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 10,
                      right: 10,
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      marginTop: '2px',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    <div
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        backgroundColor: fontId === '' ? 'var(--button-bg)' : 'transparent',
                        color: fontId === '' ? 'var(--button-text)' : 'var(--text-color)',
                        fontFamily: defaultFontFamilyCss,
                      }}
                      onClick={() => {
                        setFontId('');
                        setShowFontDropdown(false);
                      }}
                    >
                      Default (TikTok medium)
                    </div>
                    {sortedFonts.map((font) => (
                      <div
                        key={font._id}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderRadius: '6px',
                          backgroundColor: String(fontId) === String(font._id) ? 'var(--button-bg)' : 'transparent',
                          color: String(fontId) === String(font._id) ? 'var(--button-text)' : 'var(--text-color)',
                          fontFamily: fontFamilyCssForName?.(font.name) || defaultFontFamilyCss,
                        }}
                        onClick={() => {
                          setFontId(font._id);
                          setShowFontDropdown(false);
                        }}
                      >
                        {font.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Text color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={fontColor}
                      onChange={(e) => setFontColor(e.target.value)}
                      style={{
                        width: '78px',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--input-border)',
                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                        color: darkMode ? '#fff' : '#333',
                      }}
                    />
                    <input type="color" value={fontColor} onChange={(e) => setFontColor(e.target.value)} style={{ width: 30, height: 30, border: 'none', cursor: 'pointer' }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Outline</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={outlineColor}
                      onChange={(e) => setOutlineColor(e.target.value)}
                      style={{
                        width: '78px',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--input-border)',
                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                        color: darkMode ? '#fff' : '#333',
                      }}
                    />
                    <input type="color" value={outlineColor} onChange={(e) => setOutlineColor(e.target.value)} style={{ width: 30, height: 30, border: 'none', cursor: 'pointer' }} />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', alignSelf: 'flex-end', marginBottom: 4 }}>
                  <input type="checkbox" checked={bigText} onChange={(e) => setBigText(e.target.checked)} />
                  Big text
                </label>
              </div>
            </>
          )}

          {section(
            <>
              <h4 style={{ marginTop: 0 }}>5 - Export</h4>
              <p style={{ marginTop: 0, marginBottom: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Download each slide as a 1080×1350 PNG ready for Instagram / LinkedIn carousels.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="main-button"
                  onClick={handleExport}
                  disabled={exporting || slides.length === 0}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: exporting || slides.length === 0 ? 0.7 : 1,
                  }}
                >
                  {exporting ? (
                    <><span className="spinner-border" /> Exporting…</>
                  ) : (
                    <><Download size={16} /> Download all slides</>
                  )}
                </button>
                <button
                  type="button"
                  className="tertiary-button"
                  onClick={handleGenerate}
                  disabled={generating}
                  style={{ opacity: generating ? 0.7 : 1 }}
                >
                  {generating ? 'Regenerating…' : 'Regenerate text'}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {cutPickerOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setCutPickerOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--background-primary)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '520px',
              width: '95%',
              maxHeight: '85vh',
              overflow: 'auto',
              border: '1px solid var(--input-border)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)' }}>
                Select cut for slide {selectedIndex + 1}
              </h3>
              <button type="button" onClick={() => setCutPickerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-color)' }}>
                <X size={20} />
              </button>
            </div>
            {cutsLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading cuts…</div>
            ) : !cuts.length ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                No cuts in workspace. Add raw videos and generate cuts first.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
                {cuts.map((cut) => {
                  const isSelected = String(selected?.cut?._id || selected?.cut?.id) === String(cut._id || cut.id);
                  return (
                    <button
                      key={cut._id || cut.id}
                      type="button"
                      onClick={() => setSelectedCut(cut)}
                      style={{
                        padding: 0,
                        border: isSelected ? '2px solid #10b981' : '2px solid var(--input-border)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: 'none',
                      }}
                    >
                      {cut.thumbnailUrl ? (
                        <img src={cut.thumbnailUrl} alt="" style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <video src={cut.url} style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} muted preload="metadata" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const secondaryBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 14px',
  borderRadius: '8px',
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  color: 'var(--text-color)',
  fontSize: '14px',
  cursor: 'pointer',
};

function iconBtnStyle(disabled) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    borderRadius: '8px',
    border: '1px solid var(--input-border)',
    background: 'var(--input-bg)',
    color: 'var(--text-color)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
  };
}
